
import { LitElement, css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { Sample } from './sample';
import { SampleView } from './sample-view';

function icon(name: string) {
    return html`<i class="fa fa-${name}" aria-hidden="true"></i>`
}

function basename(filename: string) {
    return filename.split("/").pop()
}

const LOGOS = {
    NSF: {
        src: "NSF_logo.svg",
        title: "National Science Foundation",
        class: " h64px",
    },
    NOAA: {
        src: "noaa-oe-emblem-2024-color.png",
        title: "National Oceanic and Atmospheric Administration",
        class: " logo-background",
    },
    DOE: {
        src: "All White - Stacked horizontal DOE logo 848x253.png",
        title: "Department of Energy",
        class: " ",
    },
    ONR: {
        src: "Office_of_Naval_Research_Official_Logo.png",
        title: "Office of Naval Research",
        class: " h64px",
    },
    MMS: {
        src: "Minerals_management_service_seal.png",
        title: "Mineral Management Service",
        class: " h64px",
    },
    RI: {
        src: "Coat_of_arms_of_Rhode_Island.svg.png",
        title: "State of Rhode Island",
        class: " h64px",
    },
}

function capitalize(str: string) {
    return str.charAt(0).toUpperCase() + str.slice(1);
}

function key_fmt(x: string) {
    return x.replaceAll('_', ' ').split(/\s+/).map(capitalize).join(" ")
}

@customElement('sample-details')
export class SampleDetails extends LitElement {

    static styles = css`
:host {
}
:host :hover {
}

:host div :hover {
  background: none;
}
.name {
   font-weight: bold;
   font-size: 1.1em;
}
.cart-button {
   position: absolute;
   top: 1em;
   right: 0em;
   font-size: 1.1em;
   cursor: pointer;
}
.description {
   display: flex;
   flex-flow: row nowrap;
   gap: 0.2em;
}
.key {
   display: inline;
   font-weight: bold;
}
.field {
  margin-left: 3em;
  text-indent: -3em;
}
.space {
  height: 1em;
}
a {
  color: lightblue;
}
.action {
  display: inline;
  color: lightblue;
  cursor: pointer;
}
.subsample {
border: 1px solid white;
box-sizing: border-box;
margin: 0.5em;
padding: 0.3em;
border-radius: 0.3em;
}
details {
border-top: 1px solid rgba(255,255,255,0.3);
margin-top: 0.5em;
padding-top: 0.5em;
}
summary {
font-size: 1.1em;
font-weight: bold;
}
.fields {
margin-top: 0.5em;
font-weight: bold;
border-bottom: 1px solid rgba(255,255,255,0.3);
}
.logo {
position: absolute;
top: 5px;
right:48px;
height: 48px;
z-index: 1000;
box-sixing: border-box;
}
.logo img {
height: 100%;
}
.logo:hover { }
.logo img:hover {
}
.logo-background {
background: #ffffff60;
border-radius: 0.3em;
box-sixing: border-box;
}
.h64px {
height: 64px !important;
}
.box {
z-index: 10;
position: relative;
}
.fimages {
display: flex;
flex-direction: row;
flex-wrap: wrap;
width: 100%;
gap: 10px;
box-sizing: border-box;
justify-content: center;
padding-top: 10px;
overflow: scroll;
max-height: 400px;
}
.fimages img {
width: 100%;
}
.fimages a {
width: 33%;
}
`;

    @property({ type: Object })
    sample: Sample;

    @property({ type: Boolean })
    in_cart: boolean;


    constructor(sample: Sample, in_cart: boolean) {
        super();
        this.sample = sample;
        this.in_cart = in_cart;
    }

    is_subsample() {
        return this.sample.sub_sample_id !== undefined
    }

    location() {
        return html`${icon('location-arrow')} ${this.sample.lat.toFixed(3)}, ${this.sample.lon.toFixed(3)}`;
    }
    depth() {
        const z = this.sample.depth;
        if (!z)
            return ``;
        return html`${icon('angle-down')} ${z} m`;
    }
    cart_button() {
        if (!this.in_cart) {
            return html`<div class="cart-button" title="Add to cart" @click=${this.cart}>${icon('plus')}</div>`;
        } else {
            return html`<div class="cart-button" title="Remove from cart" @click=${this.cart}>${icon('minus')}</div>`;
        }
    }
    date() {
        if (this.is_subsample()) {
            return ""
        }
        const t = this.sample.Collection_Date;
        return html`${icon("calendar")} ${t}`;
    }

    description() {
        const txt = this.sample.Sample_Description;
        if (!txt || txt.trim() == "")
            return '';
        return html`<div class="description"><div>${icon('commenting-o')}</div><div>${txt}</div></div>`
    }

    igsn() {
        const igsn = this.sample.IGSN;
        if (!igsn || igsn.trim() == "")
            return '';
        return html`${icon('external-link')} <a href="https://doi.org/${igsn}" target="_blank">${igsn}</a>`
    }
    parent_igsn() {
        if (!this.is_subsample()) {
            return ""
        }
        const igsn = this.sample.Parent_IGSN;
        if (!igsn || igsn.trim() == "")
            return '';
        return html`${icon('external-link')} <a href="https://doi.org/${igsn}" target="_blank">${igsn}</a> (Parent)`
    }

    action(ev: CustomEvent) {
        const el = ev.target as HTMLElement;
        this.dispatchEvent(new CustomEvent('set-search', { bubbles: true, composed: true, detail: el.dataset.value }));
    }

    metadata() {
        const keys = ['Full_Sample_Name', 'Object_Type',
            'lat', 'lon', 'depth', 'Sample_Description', 'Collection_Date',
        ];
        const skips = ['Drawer_Location', 'sample_id', 'IGSN', 'MardsenSq', 'subsamples'];
        const lines = [];
        const atend = [html`<div class="fields">IMLGS /SESAR Fields</div>`]
        for (const key of Object.keys(this.sample).sort()) {
            if (keys.includes(key) || skips.includes(key))
                continue;
            const value = this.sample[key];

            if (!value)
                continue
            if (key == "cruise") {
                lines.push(html`<div class="field"><div class="key">${key_fmt(key)}</div>: <div @click=${this.action} data-value="Cruise: ${value}" class="action">${value}</div></div>`);
            } else {
                const row = html`<div class="field"><div class="key">${key_fmt(key)}</div>: ${value}</div>`;
                if (key.startsWith('IMLGS_') || key.startsWith('SESAR_')) {
                    atend.push(row)
                } else {
                    lines.push(row)
                }
            }
        }
        return [...lines, ...atend];
    }
    sample_id() {
        return this.sample.sample_id;
    }

    name() {
        return this.sample.Full_Sample_Name;
    }

    event(name: string) {
        return new CustomEvent(name, { bubbles: true, composed: true, detail: this.sample });
    }

    cart(ev: Event) {
        ev.stopPropagation();
        this.dispatchEvent(this.event('cart-action'));
    }

    subsamples() {
        let out = []
        for (const s of this.sample.subsamples) {
            const sv = new SampleView(s, false, true)
            out.push(sv)
        }
        return out
    }
    subsamples_count() {
        if (this.is_subsample()) {
            return 'Subsample'
        }
        return `Subsamples: ${this.sample.subsamples.length}`
    }
    images() {
        if (!this.sample.images || this.sample.images.length == 0) { return "" }
        let images = html``
        let n = this.sample.images.length
        for (const file of this.sample.images) {
            images = html`${images}<a href="images/${file}" title="images/${file}" target="_blank"><img src="images/${file}"></a>`
        }
        return html`<details title="images"><summary>Images (${n})</summary><div class="fimages">${images}</div></details>`
    }
    material() {
        if (!this.sample.Material) { return "" }
        return html`<div title="Material"> ${this.sample.Material} </div>`
    }
    field_name() {
        if (!this.sample.Field_Name) { return "" }
        return html`<div title="Field Name">${icon('chevron-right')} ${this.sample.Field_Name}</div>`
    }
    sesar_classification() {
        if (!this.sample.SESAR_Classification) { return "" }
        return html`<div title="SESAR Classification">${icon('angle-double-right')} ${this.sample.SESAR_Classification}</div>`
    }
    imlgs_composition() {
        if (!this.sample.IMLGS_Composition) { return "" }
        return html`<div title="IMLGS Composition">${icon('angle-double-right')} ${this.sample.IMLGS_Composition}</div>`
    }
    imlgs_texture() {
        if (!this.sample.IMLGS_Texture) { return "" }
        return html`<div title="IMLGS Texture" >${icon('angle-double-right')} ${this.sample.IMLGS_Texture}</div>`
    }
    cruise_report() {
        if (!this.sample.Cruise_Report_PDF || this.sample.Cruise_Report_PDF == 0) { return "" }
        let reports = html``
        for (const file of this.sample.Cruise_Report_PDF) {
            let base = basename(file)
            reports = html`${reports}<div>${icon('file-text-o')} Cruise Report: <a title="Cruise Report: ${base}" href="/reports/${file}" target="_blank">${base}</a></div>`
        }
        return reports
    }
    subsamples_details() {
        if (this.is_subsample()) {
            return ""
        }
        return html`<details open>
<summary>Subsamples</summary>
<div style="width: 100%; position: relative;  display: flex; flex-flow: column nowrap; padding-top: 0.5em;">
${this.subsamples()}
</div>
</details>
`
    }

    logo() {
        let logo_img = LOGOS[this.sample.agency]
        if (!logo_img) {
            return html``
        }
        return html`<img src="${logo_img.src}" class="${logo_img.class}" title="${logo_img.title}">`
    }

    render() {
        return html`
<link rel="stylesheet" href="font-awesome-4.7.0/css/font-awesome.min.css" >
<div @click=${this.detail}>
<div class="logo">${this.logo()}</div>
<div class="box">
<div class="name" > ${this.sample.Full_Sample_Name} </div>
    <div> ${this.sample.Object_Type} </div>
  ${this.material()}
  <div>${this.subsamples_count()} </div>
<div> ${this.location()}</div>
<div> ${this.depth()}</div>
<div> ${this.date()}</div>
<div> ${this.igsn()}</div>
<div> ${this.parent_igsn()}</div>
${this.field_name()}
${this.sesar_classification()}
${this.imlgs_composition()}
${this.imlgs_texture()}
<div>${this.description()}</div>
${this.cruise_report()}
<div class="space"></div>
${this.images()}
<div> ${this.cart_button()}</div>
${this.subsamples_details()}
<details open>
<summary>Metadata</summary>
${this.metadata()}
</details>
  </div>
</div>
`
    }
}
